// /architects-interior-designers: the one-stop HVAC library for architects,
// interior designers and PMCs, and the partner sign-up.
//
//   hero: the library, and the partner offers (Saheb, 10 Oct 2026: free HVAC
//   design when we execute, referral fee or trade pricing, one engineer per
//   project, the Daikin VRV experience centre)
//   six chapters, with a bar that stays on top while scrolling:
//     01 indoor unit corner      components/IndoorUnitCorner.jsx
//     02 ceiling space           components/CeilingChecker.jsx
//     03 pipe and drain space    components/DesignerDiagrams.jsx
//     04 the perfect VRV installation
//     05 outdoor units
//     06 tools and guides: printable checklist, sample heat load, guides
//   questions, links, partner form (api/consult-order.js, "partner")
//
// Text: src/data/designerLibrary.js and src/data/indoorUnits.js. The
// pre-render writes the same text for Google.

import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, Printer } from 'lucide-react';
import Helmet from '../lib/Helmet';
import LongForm from '../components/LongForm';
import CeilingChecker, { CHECKER_UNITS } from '../components/CeilingChecker';
import IndoorUnitCorner from '../components/IndoorUnitCorner';
import SampleReport from '../components/SampleReport';
import { PipeBundleDiagram, VrvInstallDiagram } from '../components/DesignerDiagrams';
import { whatsAppLink } from '../lib/quote';
import { track, FUNNEL } from '../lib/track';
import { BUSINESS } from '../data/pricing';
import { planText, unitHeightText, tonRange } from '../data/indoorUnits';
import {
  DESIGNER_SEO, DESIGNER_OFFERS, DESIGNER_ROLES, DESIGNER_CHAPTERS, UNITS_CHAPTER, CEILING_CHAPTER,
  PIPE_SPACE, VRV_STANDARD, OUTDOOR_CHAPTER, DESIGNER_LIBRARY, DESIGNER_CHECKLIST,
  DESIGNER_FAQ, INTERNAL_LINKS, EXTERNAL_LINKS, DESIGNER_SOURCES,
} from '../data/designerLibrary';

const inputCls = 'h-11 w-full rounded-lg border border-gray-300 px-3 text-base text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500';
const HERO_IMG = '/media/projects/showrooms.webp';
const chapter = (id) => DESIGNER_CHAPTERS.find((c) => c.id === id);

const SPACES = [
  { img: '/media/projects/showrooms.webp', label: 'Retail fit-outs and showrooms' },
  { img: '/media/projects/luxury-residences.webp', label: 'Homes, villas and penthouses' },
  { img: '/media/projects/co-working.webp', label: 'Offices and co-working' },
  { img: '/media/projects/commercial-buildings.webp', label: 'Commercial buildings' },
];

function printChecklist() {
  document.body.classList.add('print-only-checklist');
  const done = () => {
    document.body.classList.remove('print-only-checklist');
    window.removeEventListener('afterprint', done);
  };
  window.addEventListener('afterprint', done);
  track('checklist_print', { source: 'designers' });
  window.print();
  setTimeout(done, 1500);
}

function ChapterHead({ id, h2, intro }) {
  const c = chapter(id);
  return (
    <>
      <p className="text-[11px] font-extrabold tracking-[0.18em] text-blue-700 sm:text-xs">{c.n} · {c.title.toUpperCase()}</p>
      <h2 className="mt-1.5 text-2xl font-extrabold leading-tight tracking-tight text-gray-900 sm:text-3xl">{h2}</h2>
      {intro && <p className="mt-2 max-w-3xl text-[15px] leading-relaxed text-gray-700 sm:text-base">{intro}</p>}
    </>
  );
}

function Ticks({ items, className = '' }) {
  return (
    <ul className={`flex flex-col gap-2 ${className}`}>
      {items.map((x) => (
        <li key={x} className="flex gap-2 text-[15px] leading-relaxed text-gray-700">
          <Check className="mt-1 h-4 w-4 shrink-0 text-blue-700" strokeWidth={2.6} aria-hidden="true" />
          <span>{x}</span>
        </li>
      ))}
    </ul>
  );
}

function SmallTable({ t }) {
  return (
    <div>
      <div className="overflow-x-auto rounded-xl border border-gray-200">
        <table className="w-full border-collapse text-left text-[13px] sm:text-sm">
          <thead className="bg-gray-50">
            <tr>{t.head.map((h) => <th key={h} scope="col" className="px-3 py-2 font-semibold text-gray-900">{h}</th>)}</tr>
          </thead>
          <tbody>
            {t.rows.map((r) => (
              <tr key={r[0]} className="border-t border-gray-100 align-top">
                {r.map((c, i) => (i === 0
                  ? <th key={c} scope="row" className="px-3 py-2 font-semibold text-gray-900">{c}</th>
                  : <td key={c + i} className="px-3 py-2 text-gray-700">{c}</td>))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {t.note && <p className="mt-1.5 text-xs leading-relaxed text-gray-500">{t.note}</p>}
    </div>
  );
}

function PartnerForm() {
  const [f, setF] = useState({ role: 'architect', name: '', firm: '', phone: '', email: '', city: '', message: '', website: '' });
  const [state, setState] = useState({ busy: false, error: '', done: false });
  const set = (patch) => { setF((x) => ({ ...x, ...patch })); setState((s) => ({ ...s, error: '' })); };

  const send = async () => {
    const mobile = f.phone.replace(/\D/g, '').slice(-10);
    const gaps = [];
    if (f.name.trim().length < 2) gaps.push('your name');
    if (!/^[6-9]\d{9}$/.test(mobile)) gaps.push('a 10-digit mobile number');
    if (gaps.length) { setState({ busy: false, done: false, error: `Add ${gaps.join(' and ')}.` }); return; }
    setState({ busy: true, error: '', done: false });
    try {
      const r = await fetch('/api/consult-order', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'partner', ...f, phone: mobile }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.error || 'Could not send. Call or WhatsApp +91 77039 00307.');
      track('partner_lead', { role: f.role });
      setState({ busy: false, error: '', done: true });
    } catch (e) {
      setState({ busy: false, done: false, error: e.message });
    }
  };

  if (state.done) {
    return (
      <div aria-live="polite" className="rounded-2xl border-2 border-green-600 bg-green-50 p-5">
        <p className="text-lg font-extrabold text-gray-900">Thank you, {f.name.trim().split(/\s+/)[0]}.</p>
        <p className="mt-1 text-sm leading-relaxed text-gray-700">We&apos;ll call you on {f.phone.replace(/\D/g, '').slice(-10).replace(/(\d{5})(\d{5})/, '$1 $2')} to set up your partnership. To share drawings now, WhatsApp them to {BUSINESS.phoneDisplay}.</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3 rounded-2xl border-2 border-blue-700 bg-white p-4 sm:p-5">
      <div>
        <p className="mb-1.5 text-[13px] font-semibold text-gray-900">I am an</p>
        <div className="grid grid-cols-3 gap-1.5" role="group" aria-label="Your role">
          {DESIGNER_ROLES.map((r) => (
            <button key={r.id} type="button" aria-pressed={f.role === r.id} onClick={() => set({ role: r.id })}
              className={`h-10 rounded-lg px-1 text-[12px] sm:text-[13px] ${f.role === r.id ? 'bg-blue-700 font-bold text-white' : 'border border-gray-300 bg-white text-gray-700'}`}>
              {r.label}
            </button>
          ))}
        </div>
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <label htmlFor="pf-name" className="mb-1.5 block text-[13px] font-semibold text-gray-900">Your name</label>
          <input id="pf-name" autoComplete="name" value={f.name} onChange={(e) => set({ name: e.target.value.slice(0, 80) })} className={inputCls} />
        </div>
        <div>
          <label htmlFor="pf-firm" className="mb-1.5 block text-[13px] font-semibold text-gray-900">Firm</label>
          <input id="pf-firm" autoComplete="organization" value={f.firm} onChange={(e) => set({ firm: e.target.value.slice(0, 100) })} className={inputCls} />
        </div>
        <div>
          <label htmlFor="pf-phone" className="mb-1.5 block text-[13px] font-semibold text-gray-900">Mobile</label>
          <input id="pf-phone" type="tel" inputMode="numeric" autoComplete="tel-national" value={f.phone} onChange={(e) => set({ phone: e.target.value.replace(/[^\d+\s]/g, '').slice(0, 14) })} className={inputCls} />
        </div>
        <div>
          <label htmlFor="pf-email" className="mb-1.5 block text-[13px] font-semibold text-gray-900">Email <span className="font-normal text-gray-500">(optional)</span></label>
          <input id="pf-email" type="email" autoComplete="email" value={f.email} onChange={(e) => set({ email: e.target.value.slice(0, 120) })} className={inputCls} />
        </div>
        <div className="sm:col-span-2">
          <label htmlFor="pf-city" className="mb-1.5 block text-[13px] font-semibold text-gray-900">City <span className="font-normal text-gray-500">(optional)</span></label>
          <input id="pf-city" value={f.city} onChange={(e) => set({ city: e.target.value.slice(0, 60) })} className={inputCls} />
        </div>
        <div className="sm:col-span-2">
          <label htmlFor="pf-msg" className="mb-1.5 block text-[13px] font-semibold text-gray-900">Current project <span className="font-normal text-gray-500">(optional)</span></label>
          <textarea id="pf-msg" rows={3} value={f.message} onChange={(e) => set({ message: e.target.value.slice(0, 800) })}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-base text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500" />
        </div>
        {/* Left empty by people; bots fill it. */}
        <input type="text" name="website" tabIndex={-1} autoComplete="off" value={f.website} onChange={(e) => set({ website: e.target.value })}
          aria-hidden="true" className="absolute -left-[9999px] h-0 w-0 opacity-0" />
      </div>
      {state.error && <p role="alert" className="rounded-lg bg-red-50 px-3 py-2 text-[13px] text-red-800">{state.error}</p>}
      <button type="button" onClick={send} disabled={state.busy}
        className="h-12 rounded-xl bg-blue-700 text-base font-bold text-white hover:bg-blue-800 disabled:bg-gray-300">
        {state.busy ? 'Sending…' : 'Become a partner'}
      </button>
      <p className="text-center text-xs text-gray-600">We&apos;ll call you to set it up.</p>
    </div>
  );
}

function PartnerCard({ className = '' }) {
  return (
    <div className={`rounded-2xl bg-white p-5 text-gray-900 shadow-xl ${className}`}>
      <p className="text-lg font-extrabold">Partner with Citi Cool</p>
      <ul className="mt-3 flex flex-col gap-2.5 text-sm text-gray-700">
        {DESIGNER_OFFERS.map((o) => (
          <li key={o.title} className="flex gap-2.5">
            <Check className="mt-0.5 h-[18px] w-[18px] shrink-0 text-blue-700" strokeWidth={3} aria-hidden="true" />
            <span><b className="text-gray-900">{o.title}.</b> {o.text}</span>
          </li>
        ))}
      </ul>
      <a href="#partner" className="mt-4 flex h-12 items-center justify-center rounded-xl bg-blue-700 text-[15px] font-extrabold text-white hover:bg-blue-800">Become a partner</a>
    </div>
  );
}

// The chapter bar: stays under the site header, shows where you are.
function ChapterBar({ active, className = '' }) {
  const ref = useRef(null);
  useEffect(() => {
    const box = ref.current;
    const el = box && box.querySelector(`[data-ch="${active}"]`);
    if (!el) return;
    const left = el.offsetLeft - 16;
    if (left < box.scrollLeft || left + el.offsetWidth > box.scrollLeft + box.clientWidth - 16) {
      box.scrollTo({ left, behavior: 'smooth' });
    }
  }, [active]);
  const items = [...DESIGNER_CHAPTERS.map((c) => ({ id: c.id, label: `${c.n} ${c.short}` })), { id: 'questions', label: 'Questions' }];
  return (
    <nav aria-label="Chapters" className={`no-print sticky top-16 z-30 ${className} border-y border-gray-200 bg-white/95 shadow-[0_8px_14px_-12px_rgba(0,0,0,0.3)] backdrop-blur-sm lg:top-20`}>
      <div className="mx-auto flex max-w-6xl items-center gap-2 lg:px-4">
        <div ref={ref} className="flex min-w-0 flex-1 gap-1.5 overflow-x-auto px-4 py-2 [scrollbar-width:none] lg:px-0 [&::-webkit-scrollbar]:hidden">
          {items.map((c) => (
            <a key={c.id} href={`#${c.id}`} data-ch={c.id} aria-current={active === c.id ? 'true' : undefined}
              className={`flex h-9 shrink-0 items-center whitespace-nowrap rounded-full px-3.5 text-[13px] lg:text-sm ${active === c.id ? 'bg-blue-700 font-bold text-white' : 'border border-gray-300 font-semibold text-gray-700 hover:border-blue-400 hover:text-blue-700 lg:border-transparent'}`}>
              {c.label}
            </a>
          ))}
        </div>
        <a href="#partner" className="hidden h-10 shrink-0 items-center rounded-lg border-2 border-blue-700 px-4 text-sm font-extrabold text-blue-700 lg:flex">Become a partner</a>
      </div>
    </nav>
  );
}

export default function Architects() {
  const waHref = whatsAppLink("Hi, I'm an architect / interior designer. I'd like to share drawings for HVAC design.");
  const onWhatsApp = () => track(FUNNEL.WHATSAPP_HANDOFF, { source: 'designers_drawings' });
  const [checkUnit, setCheckUnit] = useState('slim-duct');
  const [active, setActive] = useState('units');

  // Highlight the chapter being read.
  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return undefined;
    const ids = [...DESIGNER_CHAPTERS.map((c) => c.id), 'questions'];
    const seen = new Map();
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => seen.set(e.target.id, e.isIntersecting));
      const now = ids.find((id) => seen.get(id));
      if (now) setActive(now);
    }, { rootMargin: '-150px 0px -55% 0px' });
    ids.forEach((id) => { const el = document.getElementById(id); if (el) io.observe(el); });
    return () => io.disconnect();
  }, []);

  const checkWith = (id) => {
    setCheckUnit(id);
    track('ceiling_check_from_unit', { unit: id });
    const el = document.getElementById('ceiling-tool');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <>
      <Helmet>
        <title>{DESIGNER_SEO.title}</title>
        <meta name="description" content={DESIGNER_SEO.meta} />
      </Helmet>

      <main className="pt-16 pb-16 lg:pt-20">
        {/* ─── hero ─── */}
        <section className="relative overflow-hidden bg-[#172554] text-white">
          <img src={HERO_IMG} alt="" width="960" height="576" loading="eager" fetchPriority="high"
            className="absolute inset-0 h-full w-full object-cover opacity-40" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#172554]/50 via-[#172554]/85 to-[#172554] lg:bg-gradient-to-r lg:from-[#172554] lg:via-[#172554]/90 lg:to-[#172554]/60" aria-hidden="true" />
          <div className="relative mx-auto grid max-w-6xl gap-10 px-4 pb-8 pt-24 sm:pb-12 sm:pt-28 lg:grid-cols-[minmax(0,1fr)_380px] lg:items-center lg:pb-14 lg:pt-16">
            <div>
              <nav className="text-[11px] text-blue-200 sm:text-xs">
                <Link to="/" className="hover:text-white">Home</Link><span className="mx-1.5">›</span>
                <span className="text-white">Architects and interior designers</span>
              </nav>
              <p className="mt-3 text-[11px] font-bold tracking-[0.18em] text-orange-300 sm:text-xs">{DESIGNER_SEO.eyebrow}</p>
              <h1 className="mt-2 max-w-3xl text-[28px] font-extrabold leading-[1.13] tracking-tight sm:text-5xl">{DESIGNER_SEO.h1}</h1>
              <p className="mt-3 max-w-2xl text-sm leading-relaxed text-blue-100 sm:text-[17px]">{DESIGNER_SEO.intro}</p>
              <div className="mt-5 grid grid-cols-2 gap-2 sm:flex">
                <a href="#units" className="flex h-12 items-center justify-center rounded-xl bg-white px-5 text-center text-[14px] font-extrabold text-[#1e3a8a] sm:text-[15px]">Open the indoor unit corner</a>
                <a href={waHref} target="_blank" rel="noopener noreferrer" onClick={onWhatsApp}
                  className="flex h-12 items-center justify-center rounded-xl border border-white/60 px-5 text-center text-[13px] font-semibold text-white sm:text-sm">WhatsApp us your drawings</a>
              </div>
            </div>
            <PartnerCard className="hidden lg:block" />
          </div>
        </section>

        {/* ─── start here ─── */}
        <section id="library" className="mx-auto max-w-6xl scroll-mt-36 px-4 pt-6 sm:pt-8">
          <p className="text-xs font-extrabold tracking-[0.15em] text-gray-500">START HERE: THE DESIGNERS&apos; HVAC LIBRARY</p>
          <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6 lg:gap-3">
            {DESIGNER_CHAPTERS.map((c, i) => (
              <a key={c.id} href={`#${c.id}`}
                className={`block rounded-2xl p-3.5 ${i === 0 ? 'border-[1.5px] border-blue-700 bg-blue-50' : 'border border-gray-200 hover:border-blue-400'}`}>
                <span className="block text-xs font-extrabold text-blue-700">{c.n}</span>
                <span className="mt-1 block text-[14px] font-extrabold leading-snug text-gray-900 sm:text-[15px]">{c.title}</span>
                <span className="mt-1 block text-xs leading-snug text-gray-600">{c.line}</span>
              </a>
            ))}
          </div>
        </section>

        <div className="mx-auto max-w-6xl px-4 pt-4 lg:hidden">
          <div className="rounded-2xl bg-[#172554] p-1"><PartnerCard className="shadow-none" /></div>
        </div>

        <ChapterBar active={active} className="mt-8" />

        <div className="mx-auto max-w-6xl px-4">
          {/* ─── 01 indoor unit corner ─── */}
          <section id="units" className="scroll-mt-36 pt-8 lg:pt-10">
            <ChapterHead id="units" h2={UNITS_CHAPTER.h2} intro={UNITS_CHAPTER.intro} />
            <div className="mt-5"><IndoorUnitCorner onCheck={checkWith} /></div>
          </section>

          {/* ─── 02 ceiling space ─── */}
          <section id="ceiling" className="scroll-mt-36 pt-14">
            <ChapterHead id="ceiling" h2={CEILING_CHAPTER.h2} intro={CEILING_CHAPTER.intro} />
            <div className="mt-5 grid gap-8 lg:grid-cols-[minmax(0,1fr)_420px]">
              <div className="min-w-0">
                <div className="overflow-x-auto rounded-xl border border-gray-200">
                  <table className="w-full border-collapse text-left text-[13px] sm:text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th scope="col" className="px-3 py-2 font-semibold text-gray-900">Ceiling unit</th>
                        <th scope="col" className="px-3 py-2 font-semibold text-gray-900">Unit height</th>
                        <th scope="col" className="px-3 py-2 font-semibold text-orange-800">Ceiling to plan for</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[...CHECKER_UNITS].sort((a, b) => a.plan[0] - b.plan[0]).map((u) => (
                        <tr key={u.id} className="border-t border-gray-100 align-top">
                          <th scope="row" className="px-3 py-2 font-semibold text-gray-900">{u.name}<span className="block text-xs font-normal text-gray-500">{u.series} · {tonRange(u)}</span></th>
                          <td className="px-3 py-2 text-gray-700">{unitHeightText(u)}</td>
                          <td className="px-3 py-2 font-semibold text-orange-800">{planText(u)}{u.ducts ? ' + ducts' : ''}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className="mt-1.5 text-xs leading-relaxed text-gray-500">Daikin&apos;s ceiling figure (or the unit height + 30 mm), plus 1–2 inches to service the unit. Leave 3 inches behind a 1-way cassette.</p>
                <p className="mt-5 text-[15px] leading-relaxed text-gray-700">{CEILING_CHAPTER.nbc}</p>
                <p className="mt-3 text-[15px] leading-relaxed text-gray-700">{CEILING_CHAPTER.example}</p>
                <Ticks items={CEILING_CHAPTER.tips} className="mt-4" />
              </div>
              <div className="lg:sticky lg:top-40 lg:self-start">
                <CeilingChecker unitId={checkUnit} onUnitChange={setCheckUnit} />
              </div>
            </div>
          </section>

          {/* ─── 03 pipe and drain space ─── */}
          <section id="pipes" className="scroll-mt-36 pt-14">
            <ChapterHead id="pipes" h2={PIPE_SPACE.h2} intro={PIPE_SPACE.intro} />
            <div className="mt-5 grid gap-6 lg:grid-cols-2">
              <figure className="rounded-2xl border border-slate-200 bg-slate-50 p-3 lg:self-start">
                <PipeBundleDiagram />
                <figcaption className="mt-2 text-xs leading-relaxed text-slate-600">{PIPE_SPACE.caption}</figcaption>
              </figure>
              <div>
                <h3 className="text-lg font-extrabold text-gray-900">Finished size of the pipe pair</h3>
                <div className="mt-3 flex flex-col gap-2">
                  {PIPE_SPACE.rows.map((r) => (
                    <div key={r.where} className="grid grid-cols-[1fr_auto] items-start gap-x-3 gap-y-1 rounded-xl border border-gray-200 p-3">
                      <p className="text-sm font-bold text-gray-900">{r.where}</p>
                      <p className="whitespace-nowrap text-right text-[15px] font-extrabold text-orange-700">{r.inches}<span className="block text-[11px] font-semibold text-orange-800">{r.size}</span></p>
                      <p className="col-span-2 text-xs leading-relaxed text-gray-600">{r.pipes}</p>
                    </div>
                  ))}
                </div>
                <p className="mt-2 text-xs leading-relaxed text-gray-500">{PIPE_SPACE.rowsNote}</p>
              </div>
            </div>
            <div className="mt-4 grid gap-3 lg:grid-cols-2">
              <div className="rounded-xl bg-blue-50 p-4">
                <p className="text-sm font-bold text-blue-900">{PIPE_SPACE.insulation.title}</p>
                <p className="mt-1 text-sm leading-relaxed text-blue-900">{PIPE_SPACE.insulation.text}</p>
              </div>
              <div className="rounded-xl bg-amber-50 p-4">
                <p className="text-sm font-bold text-amber-900">{PIPE_SPACE.drain.title}</p>
                <p className="mt-1 text-sm leading-relaxed text-amber-900">{PIPE_SPACE.drain.text}</p>
              </div>
            </div>
            <h3 className="mt-8 text-lg font-extrabold text-gray-900">Finishing the pipes</h3>
            <p className="mt-1 text-sm text-gray-600">Four ways pipes are finished, the space each needs, and what to watch.</p>
            <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              {PIPE_SPACE.finishes.map((f) => (
                <div key={f.title} className="rounded-2xl border border-gray-200 p-4">
                  <p className="text-[15px] font-extrabold text-gray-900">{f.title}</p>
                  <dl className="mt-2 flex flex-col gap-1 text-[13px] leading-snug">
                    <div className="grid grid-cols-[52px_1fr] gap-2"><dt className="text-gray-500">Space</dt><dd className="text-gray-900">{f.space}</dd></div>
                    <div className="grid grid-cols-[52px_1fr] gap-2"><dt className="text-gray-500">Finish</dt><dd className="text-gray-900">{f.finish}</dd></div>
                  </dl>
                  <p className="mt-2 text-[13px] leading-relaxed text-amber-900"><b>Watch out:</b> {f.watch}</p>
                </div>
              ))}
            </div>
            <div className="mt-4 rounded-xl bg-blue-50 p-4">
              <p className="text-sm font-bold text-blue-900">{PIPE_SPACE.sleeves.title}</p>
              <p className="mt-1 text-sm leading-relaxed text-blue-900">{PIPE_SPACE.sleeves.text}</p>
            </div>
            <div className="mt-6 grid gap-6 lg:grid-cols-2">
              <div>
                <h3 className="mb-2 text-base font-extrabold text-gray-900">Pipe sizes to each indoor unit</h3>
                <SmallTable t={PIPE_SPACE.connections} />
              </div>
              <div>
                <h3 className="mb-2 text-base font-extrabold text-gray-900">How far the pipes can run</h3>
                <SmallTable t={PIPE_SPACE.limits} />
              </div>
            </div>
          </section>

          {/* ─── 04 the perfect VRV installation ─── */}
          <section id="installation" className="scroll-mt-36 pt-14">
            <ChapterHead id="installation" h2={VRV_STANDARD.h2} intro={VRV_STANDARD.intro} />
            <div className="mt-5 grid gap-6 lg:grid-cols-[400px_minmax(0,1fr)]">
              <figure className="rounded-2xl border border-slate-200 bg-slate-50 p-3 lg:sticky lg:top-40 lg:self-start">
                <VrvInstallDiagram />
                <figcaption className="mt-2 text-xs text-slate-600">{VRV_STANDARD.caption}</figcaption>
              </figure>
              <div className="flex flex-col gap-6">
                {VRV_STANDARD.groups.map((g) => (
                  <div key={g.title}>
                    <h3 className="text-base font-extrabold text-gray-900">{g.title}</h3>
                    <div className="mt-2 grid gap-2 xl:grid-cols-2">
                      {g.items.map((c) => (
                        <div key={c.n} className="flex gap-3 rounded-2xl border border-gray-200 p-3.5">
                          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-orange-700 text-[13px] font-extrabold text-white">{c.n}</span>
                          <div className="min-w-0">
                            <p className="text-[15px] font-extrabold text-gray-900">{c.title}</p>
                            <p className="mt-1 text-[13px] leading-relaxed text-gray-700">{c.text}</p>
                            <span className="mt-2 flex flex-wrap gap-1.5">
                              {c.src !== 'ours' && <span className="rounded-full bg-blue-50 px-2 py-0.5 text-[11px] font-bold text-blue-700">Daikin rule</span>}
                              {c.src !== 'daikin' && <span className="rounded-full bg-orange-50 px-2 py-0.5 text-[11px] font-bold text-orange-800">Our standard</span>}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
                <div className="rounded-2xl bg-[#172554] p-5 text-white">
                  <p className="text-base font-extrabold">Want an engineer to check a site?</p>
                  <p className="mt-1 text-sm leading-relaxed text-blue-100">Site visit and a 1-hour consultation, <s className="text-blue-300">₹5,000</s> <b className="text-white">₹1,500</b>. Fee adjusted if we do the job.</p>
                  <Link to="/hvac-consultant?service=visit&from=designers" className="mt-3 inline-flex h-11 items-center rounded-xl bg-white px-5 text-sm font-extrabold text-[#1e3a8a]">Book a site visit</Link>
                </div>
                <p className="text-xs leading-relaxed text-gray-500">{VRV_STANDARD.source}</p>
              </div>
            </div>
          </section>

          {/* ─── 05 outdoor units ─── */}
          <section id="outdoor" className="scroll-mt-36 pt-14">
            <ChapterHead id="outdoor" h2={OUTDOOR_CHAPTER.h2} intro={OUTDOOR_CHAPTER.intro} />
            <div className="mt-4 grid gap-6 lg:grid-cols-[minmax(0,1fr)_380px]">
              <Ticks items={OUTDOOR_CHAPTER.items} />
              <div className="rounded-2xl bg-blue-50 p-5 lg:self-start">
                <p className="text-sm font-extrabold text-blue-900">{OUTDOOR_CHAPTER.base.title}</p>
                <p className="mt-1.5 text-sm leading-relaxed text-blue-900">{OUTDOOR_CHAPTER.base.text}</p>
              </div>
            </div>
          </section>

          {/* ─── 06 tools and guides ─── */}
          <section id="tools" className="scroll-mt-36 pt-14">
            <ChapterHead id="tools" h2="Free tools and guides" intro="A checklist to pin to the site file, our heat load report format, and short guides on systems, grilles, noise, stages, retail and heat load." />

            <div id="checklist" className="mt-6 scroll-mt-36 rounded-2xl border border-gray-200 p-4 sm:p-5">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <h3 className="text-xl font-extrabold tracking-tight text-gray-900">HVAC coordination checklist</h3>
                <button type="button" onClick={printChecklist}
                  className="no-print inline-flex h-10 items-center gap-1.5 rounded-lg border-2 border-blue-700 px-3.5 text-sm font-semibold text-blue-700">
                  <Printer className="h-4 w-4" aria-hidden="true" /> Print
                </button>
              </div>
              <p className="mt-1 text-sm text-gray-700">Pin it to the site file, and tick it off stage by stage.</p>
              <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {DESIGNER_CHECKLIST.map((g) => (
                  <div key={g.group} className="rounded-xl bg-gray-50 p-4">
                    <p className="text-sm font-bold text-gray-900">{g.group}</p>
                    <ul className="mt-2 flex flex-col gap-2">
                      {g.items.map((x) => (
                        <li key={x} className="flex gap-2 text-sm leading-snug text-gray-700">
                          <span aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0 rounded border-2 border-gray-400 bg-white" />
                          <span>{x}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
              <p className="print-only mt-4 hidden text-xs text-gray-600">Citi Cool Corporation · {BUSINESS.phoneDisplay} · citicool.in/architects-interior-designers</p>
            </div>

            <div className="mt-8">
              <h3 className="text-xl font-extrabold tracking-tight text-gray-900">Our heat load report</h3>
              <p className="mt-1 max-w-3xl text-sm leading-relaxed text-gray-700">Room by room and season by season, following the ASHRAE and ISHRAE methods used in Daikin&apos;s heat load sheet. Values are hidden in the sample; your report has every number for your project.</p>
              <div className="mt-3"><SampleReport /></div>
              <Link to="/hvac-consultant?service=heatload&from=designers" className="mt-3 inline-flex text-sm font-bold text-blue-700 hover:underline">Order a heat load calculation, ₹0.50 a sq ft ›</Link>
            </div>

            <div className="mt-8">
              <h3 className="mb-3 text-xl font-extrabold tracking-tight text-gray-900">Guides</h3>
              <LongForm sections={DESIGNER_LIBRARY} folded />
            </div>
          </section>

          {/* ─── proof ─── */}
          <section className="pt-14">
            <h2 className="text-xl font-extrabold tracking-tight text-gray-900 sm:text-2xl">Spaces we do with designers</h2>
            <div className="mt-4 grid grid-cols-2 gap-3 lg:grid-cols-4">
              {SPACES.map((s) => (
                <div key={s.label} className="overflow-hidden rounded-xl border border-gray-200">
                  <img src={s.img} alt="" width="960" height="576" loading="lazy" className="aspect-[5/3] w-full object-cover" />
                  <p className="px-3 py-2 text-[13px] font-semibold text-gray-900">{s.label}</p>
                </div>
              ))}
            </div>
            <p className="mt-2 text-xs text-gray-500">Also restaurants, cafes, clinics and hotels. Images are illustrative.</p>
            <div className="mt-6 grid grid-cols-2 gap-2 sm:grid-cols-4">
              {[[String(BUSINESS.since), 'Cooling Delhi since'], [String(BUSINESS.daikinSince), 'Authorized Daikin dealer'], ['#1', 'Daikin dealer, North Region'], ['1,500+', 'Projects done']].map(([a, b]) => (
                <div key={b} className="rounded-xl bg-[#172554] p-3.5 text-white">
                  <p className="text-xl font-extrabold">{a}</p>
                  <p className="mt-0.5 text-[11px] text-blue-200">{b}</p>
                </div>
              ))}
            </div>
          </section>

          {/* ─── questions ─── */}
          <section id="questions" className="scroll-mt-36 pt-14">
            <h2 className="text-xl font-extrabold tracking-tight text-gray-900 sm:text-2xl">Questions designers ask</h2>
            <div className="mt-3 grid gap-x-8 border-t border-gray-200 lg:grid-cols-2">
              {[DESIGNER_FAQ.slice(0, Math.ceil(DESIGNER_FAQ.length / 2)), DESIGNER_FAQ.slice(Math.ceil(DESIGNER_FAQ.length / 2))].map((col, ci) => (
                <div key={ci} className="divide-y divide-gray-200 border-b border-gray-200">
                  {col.map((x) => (
                    <details key={x.q} className="group">
                      <summary className="flex cursor-pointer list-none items-center justify-between gap-3 py-3.5 text-left text-[15px] font-medium text-gray-900 [&::-webkit-details-marker]:hidden">
                        {x.q}
                        <svg viewBox="0 0 20 20" className="h-4 w-4 shrink-0 text-gray-400 transition-transform group-open:rotate-180" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true"><path d="M5 8l5 5 5-5" strokeLinecap="round" strokeLinejoin="round" /></svg>
                      </summary>
                      <p className="pb-4 text-sm leading-relaxed text-gray-600">{x.a}</p>
                    </details>
                  ))}
                </div>
              ))}
            </div>
          </section>

          {/* ─── links ─── */}
          <section id="links" className="scroll-mt-36 pt-14">
            <div id="sources" className="scroll-mt-36 rounded-2xl border border-gray-200 bg-gray-50 p-4 sm:p-5">
              <h2 className="text-lg font-extrabold tracking-tight text-gray-900 sm:text-xl">Where these figures come from</h2>
              <p className="mt-1 text-sm text-gray-600">Every figure on this page is from Daikin, a government code, or our own projects. Nothing is estimated.</p>
              <ul className="mt-3 grid gap-3 sm:grid-cols-2">
                {DESIGNER_SOURCES.map((x) => (
                  <li key={x.label} className="text-sm leading-snug">
                    {x.href
                      ? <a href={x.href} target="_blank" rel="noopener noreferrer" className="font-semibold text-blue-700 hover:underline">{x.label} ↗</a>
                      : <span className="font-semibold text-gray-900">{x.label}</span>}
                    <span className="block text-xs text-gray-600">{x.used}</span>
                  </li>
                ))}
              </ul>
            </div>
            <h2 className="mt-10 text-xl font-extrabold tracking-tight text-gray-900 sm:text-2xl">Resources and important links</h2>
            <div className="mt-4 grid gap-6 sm:grid-cols-2">
              <div>
                <p className="text-xs font-bold uppercase tracking-wider text-gray-500">Daikin manuals, codes and standards</p>
                <ul className="mt-2 flex flex-col gap-2.5">
                  {EXTERNAL_LINKS.map((l) => (
                    <li key={l.href}><a href={l.href} target="_blank" rel="noopener noreferrer" className="text-sm font-semibold text-blue-700 hover:underline">{l.label} ↗</a><span className="block text-xs text-gray-600">{l.note}</span></li>
                  ))}
                </ul>
              </div>
              <div>
                <p className="text-xs font-bold uppercase tracking-wider text-gray-500">Our tools and pages</p>
                <ul className="mt-2 flex flex-col gap-2.5">
                  {INTERNAL_LINKS.map((l) => (
                    <li key={l.href}><Link to={l.href} className="text-sm font-semibold text-blue-700 hover:underline">{l.label}</Link><span className="block text-xs text-gray-600">{l.note}</span></li>
                  ))}
                </ul>
              </div>
            </div>
          </section>

          {/* ─── partner ─── */}
          <section id="partner" className="scroll-mt-36 pt-14">
            <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)] lg:items-start">
              <div>
                <h2 className="text-2xl font-extrabold tracking-tight text-gray-900 sm:text-3xl">Become a partner</h2>
                <p className="mt-1 text-[15px] text-gray-700">Free HVAC design when we do the job, a referral fee or trade pricing, and one engineer for every project.</p>
                <div className="mt-4 grid grid-cols-2 gap-3">
                  {DESIGNER_OFFERS.map((o) => (
                    <div key={o.title} className="rounded-2xl border border-gray-200 p-4">
                      <p className="text-2xl font-extrabold text-orange-700">{o.mark}</p>
                      <p className="mt-1.5 text-[15px] font-bold text-gray-900">{o.title}</p>
                      <p className="mt-1 text-[13px] leading-relaxed text-gray-600">{o.text}</p>
                    </div>
                  ))}
                </div>
              </div>
              <PartnerForm />
            </div>
          </section>
        </div>
      </main>
    </>
  );
}
